import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getEjercicio } from "@/lib/api";

// "Modo asiento" (navegación tipo SAP): activo cuando el ejercicio tiene el
// DIARIO guardado (origen_datos HOLDED por API o HOLDED_EXCEL por Libro
// diario). Permite saltar desde cualquier cuenta del análisis (PyG, Balance,
// Flujos…) a su MAYOR en el libro contable, y de ahí al asiento completo.
// En ejercicios sin diario (Excel S&S, Finoview) devuelve false y las vistas
// se quedan como siempre.
export default function useModoAsiento(ejercicioId) {
  const navigate = useNavigate();
  const [modoAsiento, setModoAsiento] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (!ejercicioId) { setModoAsiento(false); return; }
    (async () => {
      try {
        const { data } = await getEjercicio(ejercicioId);
        if (vivo) setModoAsiento(Boolean(data?.origen_datos?.startsWith("HOLDED")));
      } catch {
        if (vivo) setModoAsiento(false);
      }
    })();
    return () => { vivo = false; };
  }, [ejercicioId]);

  const irAlMayor = useCallback(
    (codigo) => navigate(`/ejercicios/${ejercicioId}/contabilidad?cuenta=${encodeURIComponent(codigo)}`),
    [navigate, ejercicioId]
  );

  return { modoAsiento, irAlMayor };
}

// Cuentas sintéticas de ASIENTOS MANUALES: llevan el sufijo interno "-AJ<id>"
// (lo genera routers/asientos.py para garantizar unicidad y trazabilidad).
// Es fontanería nuestra, no información del usuario: en pantalla se recorta
// y se sustituye por el símbolo ✎ con tooltip (petición Héctor + Pau,
// sept-2026: "eliminar el código largo y poner un símbolo").
const RE_SUFIJO_ASIENTO = /-AJ\w+$/;

export function esCuentaDeAsiento(codigo) {
  return RE_SUFIJO_ASIENTO.test(String(codigo || ""));
}

export function codigoVisible(codigo) {
  return String(codigo || "").replace(RE_SUFIJO_ASIENTO, "");
}

// Símbolo que marca una cuenta nacida de un asiento manual.
export function SimboloAsiento({ className = "" }) {
  return (
    <span
      className={`text-primary/80 cursor-help select-none ${className}`}
      title="Proviene de un asiento manual (ajuste). El importe es independiente de la cuenta original."
      aria-label="Cuenta de asiento manual"
    >
      ✎
    </span>
  );
}

// Celda de código de cuenta reutilizable: clicable en modo asiento (abre el
// mayor de la cuenta), texto plano en ejercicios sin diario. Las cuentas de
// asiento manual se muestran sin el sufijo -AJ y con el símbolo ✎.
export function CodigoCuenta({ codigo, onVerMayor, className = "" }) {
  const esAsiento = esCuentaDeAsiento(codigo);
  const texto = codigoVisible(codigo);
  const marca = esAsiento ? <SimboloAsiento className="ml-1" /> : null;
  if (!onVerMayor) {
    return (
      <span className={className}>
        {texto}
        {marca}
      </span>
    );
  }
  return (
    <span className={className}>
      <button
        type="button"
        className="underline decoration-dotted underline-offset-2 hover:text-primary text-left"
        title="Ver el mayor de esta cuenta (apuntes y asientos)"
        onClick={(e) => {
          e.stopPropagation();
          onVerMayor(codigo);
        }}
      >
        {texto}
      </button>
      {marca}
    </span>
  );
}
